import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, Coffee, Building2, MapPin, Video } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { AmbientStatus } from "./AmbientStatus";
import { CaptchaField } from "./CaptchaField";
import { KineticWords } from "./KineticWords";

const API =   `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
const MODE_ICONS = { office: Building2, visit: MapPin, online: Video };

const inputCls =
  "w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400/20";

export const Booking = () => {
  const { t, lang } = useLanguage();
  const [advisor, setAdvisor] = useState("any");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("office");
  const [taken, setTaken] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "" });
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState({ id: null, text: "" });
  const [capRefresh, setCapRefresh] = useState(0);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!date) return;
    setTime("");
    axios
      .get(`${API}/bookings/slots`, { params: { date } })
      .then((r) => setTaken(r.data.booked || []))
      .catch(() => setTaken([]));
  }, [date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const advisors = [
    { id: "raffaele", name: t.about.partner1Name, initials: "RZ", testid: "advisor-card-raffaele" },
    { id: "ondrej", name: t.about.partner2Name, initials: "OŠ", testid: "advisor-card-ondrej" },
    { id: "any", name: t.booking.advisorBoth[lang], initials: "2×", testid: "advisor-card-any" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error(t.booking.missingSlot[lang]);
      return;
    }
    if (!captcha.id || captcha.text.trim().length < 4) {
      toast.error(t.booking.captchaMissing[lang]);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        advisor, date, time, mode,
        name: form.name, email: form.email, phone: form.phone,
        topic: form.topic || null, lang,
        captchaId: captcha.id, captchaText: captcha.text,
      });
      toast.success(t.booking.successTitle[lang], {
        description: `${advisors.find((a) => a.id === advisor).name} · ${date} · ${time} · ${t.booking.modes[mode][lang]} — ${t.booking.successMsg[lang]}`,
      });
      setForm({ name: "", email: "", phone: "", topic: "" });
      setTime("");
      setTaken([...taken, time]);
    } catch (err) {
      if (err?.response?.data?.detail === "invalid_captcha") {
        toast.error(t.booking.captchaWrong[lang]);
      } else {
        toast.error(t.booking.errorMsg[lang]);
      }
      setCapRefresh((k) => k + 1);
    } finally {
      setSending(false);
    }
  };

  const pickBtn = (active) =>
    `rounded-xl border p-4 text-center transition-all duration-200 ${
      active
        ? "border-teal-400 bg-teal-400/15 shadow-[0_0_0_3px_rgba(45,212,191,0.18),0_0_20px_rgba(45,212,191,0.25)]"
        : "border-teal-400/20 bg-white/5 hover:border-teal-300/50"
    }`;

  return (
    <section id="booking" data-testid="booking-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-16 h-[400px] w-[640px] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "radial-gradient(circle, rgba(0,245,212,0.08) 0%, transparent 70%)" }} aria-hidden="true" />
      <div className="mesh-orb orb-b absolute -left-40 top-1/3 h-[440px] w-[440px] rounded-full" style={{ background: "rgba(13,148,136,0.13)" }} aria-hidden="true" />
      <div className="mesh-orb orb-a absolute -right-32 top-0 h-[360px] w-[360px] rounded-full" style={{ background: "rgba(45,212,191,0.10)" }} aria-hidden="true" />
      <div className="laser-divider absolute inset-x-0 top-0" aria-hidden="true" />
      <AmbientStatus className="left-[45%] top-16" />

      <CalendarDays size={44} strokeWidth={1.2} className="float-slow absolute left-[5%] top-24 text-teal-400/40" aria-hidden="true" />
      <Clock size={40} strokeWidth={1.2} className="float-slow absolute right-[6%] top-36 text-teal-400/40" style={{ animationDelay: "1.1s" }} aria-hidden="true" />
      <Video size={38} strokeWidth={1.2} className="float-slow absolute bottom-40 left-[7%] text-teal-400/40" style={{ animationDelay: "2s" }} aria-hidden="true" />
      <MapPin size={42} strokeWidth={1.2} className="float-slow absolute bottom-52 right-[8%] text-teal-300/45" style={{ animationDelay: "0.7s" }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12">
        <div className="max-w-4xl">
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-teal-400">
            05 · {t.booking.badge[lang]}
          </span>
          <h2 data-testid="booking-heading" className="font-display mt-6 text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <KineticWords key={lang} text={t.booking.heading[lang]} />
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{t.booking.subtext[lang]}</p>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-teal-300" />{t.booking.duration[lang]}</span>
            <span className="flex items-center gap-1.5"><Coffee size={14} className="text-teal-300" />{t.booking.modeLabel[lang]}</span>
          </div>
        </div>

        <form
          onSubmit={submit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.target.type !== "submit") e.preventDefault();
          }}
          data-testid="booking-form"
          className="glass-dark mx-auto mt-16 max-w-5xl rounded-3xl p-6 shadow-2xl sm:p-10"
        >
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <p className="text-sm font-bold text-white">{t.booking.stepAdvisor[lang]}</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {advisors.map((a) => (
                  <button
                    type="button"
                    key={a.id}
                    data-testid={a.testid}
                    onClick={() => setAdvisor(a.id)}
                    className={pickBtn(advisor === a.id)}
                  >
                    <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full font-mono text-sm font-bold ${
                      advisor === a.id ? "bg-teal-500 text-[#052E2B]" : "border border-teal-400/30 bg-[#0A192F] text-teal-300"
                    }`}>
                      {a.initials}
                    </span>
                    <span className="mt-3 block text-xs font-semibold leading-tight text-slate-200">{a.name}</span>
                  </button>
                ))}
              </div>

              <p className="mt-8 text-sm font-bold text-white">{t.booking.stepWhen[lang]}</p>
              <div className="mt-4 flex items-center gap-3">
                <CalendarDays size={18} className="shrink-0 text-teal-300" />
                <input
                  type="date"
                  min={today}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="booking-date-input"
                  className={`${inputCls} max-w-[220px] [color-scheme:dark]`}
                />
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2" data-testid="booking-time-slots">
                {TIME_SLOTS.map((slot) => {
                  const isTaken = taken.includes(slot);
                  return (
                    <button
                      type="button"
                      key={slot}
                      disabled={!date || isTaken}
                      data-testid={`time-slot-${slot.replace(":", "")}`}
                      onClick={() => setTime(slot)}
                      className={`rounded-lg border py-2.5 font-mono text-xs font-semibold transition-all duration-150 ${
                        time === slot
                          ? "border-teal-400 bg-teal-500 text-[#052E2B]"
                          : isTaken
                            ? "cursor-not-allowed border-slate-700/60 bg-white/[0.03] text-slate-600 line-through"
                            : "border-teal-400/20 bg-white/5 text-slate-300 hover:border-teal-300/50 disabled:cursor-not-allowed disabled:text-slate-600"
                      }`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
              {!date && <p className="mt-3 text-xs text-slate-500">{t.booking.pickTime[lang]}</p>}

              <p className="mt-8 text-sm font-bold text-white">{t.booking.stepMode[lang]}</p>
              <div className="mt-4 grid grid-cols-3 gap-3" data-testid="booking-mode-picker">
                {Object.entries(t.booking.modes).map(([key, label]) => {
                  const Icon = MODE_ICONS[key];
                  return (
                    <button
                      type="button"
                      key={key}
                      data-testid={`mode-${key}-button`}
                      onClick={() => setMode(key)}
                      className={`flex flex-col items-center gap-2 px-3 py-4 ${pickBtn(mode === key)}`}
                    >
                      <Icon size={20} className={mode === key ? "text-teal-300" : "text-slate-500"} />
                      <span className="text-xs font-semibold leading-tight text-slate-200">{label[lang]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-sm font-bold text-white">{t.booking.stepDetails[lang]}</p>
              <div className="mt-4 space-y-4">
                <input required value={form.name} onChange={set("name")} placeholder={t.booking.namePh[lang]} aria-label={t.booking.name[lang]} className={inputCls} data-testid="booking-name-input" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required type="email" value={form.email} onChange={set("email")} placeholder={t.booking.emailPh[lang]} aria-label={t.booking.email[lang]} className={inputCls} data-testid="booking-email-input" />
                  <input required type="tel" value={form.phone} onChange={set("phone")} placeholder={t.booking.phonePh[lang]} aria-label={t.booking.phone[lang]} className={inputCls} data-testid="booking-phone-input" />
                </div>
                <textarea rows={3} value={form.topic} onChange={set("topic")} placeholder={t.booking.topicPh[lang]} aria-label={t.booking.topic[lang]} className={`${inputCls} resize-none`} data-testid="booking-topic-input" />
                <CaptchaField
                  onChange={(id, text) => setCaptcha({ id, text })}
                  inputCls={inputCls}
                  refreshSignal={capRefresh}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-button"
                className="mt-6 w-full rounded-full bg-teal-500 py-3.5 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.35)] disabled:opacity-60"
              >
                {sending ? t.booking.sending[lang] : t.booking.submit[lang]}
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">{t.booking.gdpr[lang]}</p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};
